import { env } from './env.ts';

/**
 * 구글 분석 도구 연결.
 *
 * 측정 ID 앞자리로 무엇인지 가린다.
 *
 *   `G-XXXXXXXXXX`   GA4 측정 ID  — gtag.js 를 직접 붙인다
 *   `GTM-XXXXXXX`    태그 매니저  — 컨테이너를 붙이고 dataLayer 로 알린다
 *
 * 둘은 **다른 물건**이다. 태그 매니저는 태그를 담는 그릇이고, 그 안에서 GA4 를
 * 따로 설정해야 실제 통계가 쌓인다. 어느 쪽을 넣어도 동작하게 해 두었다.
 *
 * ★ **SPA 라 자동 집계만 믿으면 안 된다.** 문서가 한 번만 뜨므로 화면이 바뀌어도
 *   아무것도 보내지 않는다. 그래서 자동 전송을 끄고 라우트가 바뀔 때마다 직접
 *   보낸다(`app.tsx` 의 `onChange`).
 *
 * ★ 측정 ID 는 **비밀이 아니다.** 원래 브라우저에 드러나는 값이라 번들에 실려도 된다.
 *
 * ★ 비워 두면 아무것도 하지 않는다.
 */

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

let started = false;

/** 태그 매니저 컨테이너인가 */
function isTagManager(id: string): boolean {
  return id.toUpperCase().startsWith('GTM-');
}

/**
 * 분석 스크립트를 한 번만 붙인다.
 *
 * 실패해도 앱은 그대로 돌아간다 — 분석은 있으면 좋은 것이지 없으면 안 되는 것이 아니다.
 */
export function initAnalytics(): void {
  const id = env.gaId;
  if (!id || started) return;
  if (typeof document === 'undefined') return;

  started = true;
  window.dataLayer = window.dataLayer ?? [];

  const script = document.createElement('script');
  script.async = true;

  if (isTagManager(id)) {
    // 태그 매니저는 dataLayer 에 시작 표시를 먼저 넣어야 한다.
    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });
    script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(id)}`;
  } else {
    // gtag 는 arguments 객체를 그대로 밀어 넣는 것이 규약이다. 화살표 함수로 바꾸면
    // arguments 가 없어 동작하지 않는다.
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer?.push(arguments);
    };
    window.gtag('js', new Date());
    // 화면 이동은 우리가 직접 보낸다. 위 설명 참고.
    window.gtag('config', id, { send_page_view: false });

    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  }

  document.head.appendChild(script);
}

/**
 * 화면 하나를 봤다고 알린다.
 *
 * 태그 매니저를 쓰면 `page_view` 라는 **맞춤 이벤트**로 들어간다. 태그 매니저에서
 * 그 이름을 트리거로 잡아 GA4 태그에 연결해야 통계에 쌓인다.
 *
 * @param path `/mission` 처럼 앱 안의 주소
 */
export function trackPageView(path: string): void {
  const id = env.gaId;
  if (!id) return;

  const payload = {
    page_path: path,
    page_location: `${window.location.origin}${path}`,
    page_title: document.title,
  };

  if (isTagManager(id)) {
    window.dataLayer?.push({ event: 'page_view', ...payload });
    return;
  }

  window.gtag?.('event', 'page_view', payload);
}

/**
 * 우리가 따로 세고 싶은 행동.
 *
 * 미션 재생·적립처럼 화면 이동만으로는 알 수 없는 것에 쓴다.
 * 예: `trackEvent('mission_reward', { campaign: 12, amount: 50 })`
 *
 * ★ 개인을 식별할 수 있는 값(이름·전화번호·이메일)은 넣지 않는다. 구글 약관이
 *   금지하고, 넣으면 계정이 정지될 수 있다.
 */
export function trackEvent(name: string, params: Record<string, unknown> = {}): void {
  const id = env.gaId;
  if (!id) return;

  if (isTagManager(id)) {
    window.dataLayer?.push({ event: name, ...params });
    return;
  }

  window.gtag?.('event', name, params);
}
